import {
  AppAccessMobileApp,
  AppAccessMode,
  AppAccessPurpose,
} from "@ots-frontend/ots-data";
import {LinkIcon, UserGroupIcon} from "@heroicons/react/20/solid";
import {useEffect, useState} from "react";
import {OtsActionButton} from "../components/OtsButton";
import {OtsForm, OtsFormActions, OtsFormRow, OtsFormSection} from "../components/OtsForm";
import {OtsSelect} from "../components/OtsSelect";
import {OtsTextInput} from "../components/OtsTextInput";
import {AppAccessAppSelector} from "./AppAccessAppSelector";

const EXPIRY_OPTIONS = [
  {value: "15", label: "15 minutes"},
  {value: "30", label: "30 minutes"},
  {value: "60", label: "1 hour"},
  {value: "240", label: "4 hours"},
  {value: "1440", label: "24 hours"},
];

export type AppAccessIdentityOption = {
  id: string;
  label: string;
};

export type AppAccessPackageFormValue = {
  name: string;
  mode: AppAccessMode;
  purpose: AppAccessPurpose;
  selectedApps: AppAccessMobileApp[];
  appIdentityId: string;
  expiresMinutes: number;
};

export type AppAccessPackageFormProps = {
  appIdentities: AppAccessIdentityOption[];
  initialMode?: AppAccessMode;
  initialSelectedApps?: AppAccessMobileApp[];
  isSubmitting?: boolean;
  onSubmit: (value: AppAccessPackageFormValue) => void | Promise<void>;
  className?: string;
};

/** Create-package form kept independent from API and region-context concerns. */
export const AppAccessPackageForm = ({
  appIdentities,
  initialMode = "ONE_TIME_CREDENTIAL",
  initialSelectedApps = ["FIELD_GO"],
  isSubmitting = false,
  onSubmit,
  className,
}: AppAccessPackageFormProps) => {
  const [name, setName] = useState(initialMode === "CONFIGURATION" ? "Team onboarding" : "App setup");
  const [mode, setMode] = useState<AppAccessMode>(initialMode);
  const [selectedApps, setSelectedApps] = useState<AppAccessMobileApp[]>(initialSelectedApps);
  const [appIdentityId, setAppIdentityId] = useState(appIdentities[0]?.id ?? "");
  const [expiresMinutes, setExpiresMinutes] = useState(15);
  const purpose: AppAccessPurpose = "INSTALL_AND_CONFIGURE";

  useEffect(() => {
    if (!appIdentityId && appIdentities.length > 0) setAppIdentityId(appIdentities[0].id);
  }, [appIdentityId, appIdentities]);

  const submit = () => {
    void onSubmit({
      name,
      mode,
      purpose,
      selectedApps,
      appIdentityId,
      expiresMinutes,
    });
  };

  const oneTime = mode === "ONE_TIME_CREDENTIAL";
  return (
    <OtsForm
      aria-label={oneTime ? "New one-time app setup" : "New onboarding kit"}
      className={className}
      onSubmit={submit}
      actions={<OtsFormActions submitLabel={oneTime ? "Create secure link" : "Create onboarding kit"} submitting={isSubmitting}/>}>
      <OtsFormSection
        title={oneTime ? "Create a one-time app setup" : "Create a reusable onboarding kit"}
        description={oneTime
          ? "For one person. The secure link expires and can only be used once."
          : "For a team or implementation. Email it in bulk or print the PDF for repeated use."}>
        <div>
          <span className="text-sm font-semibold">Delivery workflow</span>
          <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Delivery workflow">
            <OtsActionButton
              variant={oneTime ? "tonal" : "secondary"}
              icon={<LinkIcon/>}
              onClick={() => setMode("ONE_TIME_CREDENTIAL")}
              aria-pressed={oneTime}>
              One person
            </OtsActionButton>
            <OtsActionButton
              variant={!oneTime ? "tonal" : "secondary"}
              icon={<UserGroupIcon/>}
              onClick={() => setMode("CONFIGURATION")}
              aria-pressed={!oneTime}>
              Reusable onboarding
            </OtsActionButton>
          </div>
        </div>
        <OtsFormRow>
          <OtsTextInput
            label="Internal label"
            description="Helps your team find this setup later."
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.currentTarget.value)}
            required/>
          {oneTime && (
            <OtsSelect
              label="Link expires after"
              description="Starts when you create the link."
              options={EXPIRY_OPTIONS}
              value={String(expiresMinutes)}
              onChange={(next) => next && setExpiresMinutes(Number(next))}
              required/>
          )}
        </OtsFormRow>
      </OtsFormSection>
      <OtsFormSection title="Apps and identity" description="Which apps to set up and the account they sign in with.">
        <AppAccessAppSelector value={selectedApps} onChange={setSelectedApps}/>
        <OtsSelect
          label="App identity"
          description="Showing active TABLET-backed identities for the current region. The server manages the credential and endpoint details."
          placeholder="Select an app identity…"
          options={appIdentities.map((identity) => ({value: identity.id, label: identity.label}))}
          value={appIdentityId || undefined}
          onChange={(next) => setAppIdentityId(next ?? "")}
          required
          className="max-w-xl"/>
      </OtsFormSection>
    </OtsForm>
  );
};
